(ns iris.data.api
  "Public API for data access operations.
   
   This facade routes operations to the appropriate data source (Firebase or WebSocket)
   based on feature flag configuration. All application code should use this API
   instead of calling Firebase or WebSocket implementations directly."
  (:require
   [iris.data.protocol :as proto]
   [iris.data.config :as config]
   [iris.data.firebase-impl :as firebase-impl]
   [iris.data.websocket-impl :as websocket-impl]))

;; Data source instances
(defonce firebase-ds (firebase-impl/create-firebase-datasource))
(defonce websocket-ds (websocket-impl/create-websocket-datasource))

(defn- get-datasource
  "Get the appropriate data source for a feature based on config"
  [feature]
  (if (config/using-websocket? feature)
    websocket-ds
    firebase-ds))

;; ============================================================================
;; Raids
;; ============================================================================

(defn set-raid2eid
  "Map raid abbreviation to entity ID"
  [abbr eid]
  (proto/set-raid2eid (get-datasource :raids) abbr eid))

(defn get-raid2eid
  "Get entity ID for raid abbreviation. Returns promise."
  [abbr]
  (proto/get-raid2eid (get-datasource :raids) abbr))

(defn get-eid2raid
  "Get raid abbreviation for entity ID. Returns promise."
  [eid]
  (proto/get-eid2raid (get-datasource :raids) eid))

(defn reset-eid2raid
  "Clear all eid to raid mappings"
  []
  (proto/reset-eid2raid (get-datasource :raids)))

(defn reset-raid2eid
  "Clear all raid to eid mappings"
  []
  (proto/reset-raid2eid (get-datasource :raids)))

(defn reset-raid-cache-id
  "Reset raid cache ID with current timestamp"
  []
  (proto/reset-raid-cache-id (get-datasource :raids)))

;; ============================================================================
;; Votes
;; ============================================================================

(defn get-post-votes
  "Get vote counts for a post. Calls on-update-handler with result."
  [ws forum thread nick on-update-handler]
  (proto/get-post-votes (get-datasource :votes) ws forum thread nick on-update-handler))

(defn get-post-votes-promise
  "Get vote counts for a post. Returns promise."
  [ws forum thread nick]
  (proto/get-post-votes-promise (get-datasource :votes) ws forum thread nick))

;; ============================================================================
;; Replies
;; ============================================================================

(defn get-replies
  "Get reply count and timestamp. Calls on-update-handler with result."
  [ws thread on-update-handler]
  (proto/get-replies (get-datasource :replies) ws thread on-update-handler))

(defn get-replies-promise
  "Get reply count and timestamp. Returns promise."
  [ws thread]
  (proto/get-replies-promise (get-datasource :replies) ws thread))

(defn get-replies-t-promise
  "Get reply timestamp only. Returns promise."
  [ws thread]
  (proto/get-replies-t-promise (get-datasource :replies) ws thread))

;; ============================================================================
;; Shares
;; ============================================================================

(defn get-shares-promise
  "Get share counts. Returns promise."
  [ws share-page-kind ruid]
  (proto/get-shares-promise (get-datasource :shares) ws share-page-kind ruid))

;; ============================================================================
;; Admin - Votes & Shares Logs
;; ============================================================================

(defn push-admin-votes-log
  "Log admin vote action"
  [data]
  (proto/push-admin-votes-log (get-datasource :votes) data))

(defn push-admin-shares-log
  "Log admin share action"
  [data]
  (proto/push-admin-shares-log (get-datasource :shares) data))

;; ============================================================================
;; Thread Alerts
;; ============================================================================

(defn set-thread-follow
  "Set thread follow/alert"
  [data]
  (proto/set-thread-follow (get-datasource :thread-alerts) data))

(defn remove-thread-follow
  "Remove thread follow/alert"
  [id]
  (proto/remove-thread-follow (get-datasource :thread-alerts) id))

(defn set-thread-follow-t
  "Update thread follow timestamp"
  [id t]
  (proto/set-thread-follow-t (get-datasource :thread-alerts) id t))

(defn get-thread-follow-promise
  "Get thread follow data. Returns promise."
  [id]
  (proto/get-thread-follow-promise (get-datasource :thread-alerts) id))

(defn get-all-thread-follow-promise
  "Get all thread follows for user. Returns promise."
  [uid]
  (proto/get-all-thread-follow-promise (get-datasource :thread-alerts) uid))

;; ============================================================================
;; User Defaults
;; ============================================================================

(defn set-defaults-ws
  "Set default workspace"
  [ws]
  (proto/set-defaults-ws (get-datasource :user-defaults) ws))

(defn get-defaults-ws-promise
  "Get default workspace. Returns promise."
  []
  (proto/get-defaults-ws-promise (get-datasource :user-defaults)))

(defn get-default-ws-with-fallback
  "Get default workspace with fallback. Returns promise."
  [default]
  (proto/get-default-ws-with-fallback (get-datasource :user-defaults) default))

;; ============================================================================
;; User Actions
;; ============================================================================

(defn set-last-action
  "Record last user action"
  [data]
  (proto/set-last-action (get-datasource :user-actions) data))

;; ============================================================================
;; Moderation Logs
;; ============================================================================

(defn push-admin-all-moderation-log
  "Push to all-users moderation log"
  [data]
  (proto/push-admin-all-moderation-log (get-datasource :moderation-logs) data))

(defn push-admin-moderation-log
  "Push to personal moderation log"
  [data]
  (proto/push-admin-moderation-log (get-datasource :moderation-logs) data))

(defn get-admin-moderation-logs-promise
  "Get personal moderation logs. Returns promise."
  [ws uid]
  (proto/get-admin-moderation-logs-promise (get-datasource :moderation-logs) ws uid))

(defn get-admin-all-moderation-logs-promise
  "Get all-users moderation logs. Returns promise."
  [ws ruid]
  (proto/get-admin-all-moderation-logs-promise (get-datasource :moderation-logs) ws ruid))
